import { Four, lengthEq, maybe, Triple } from '../util.js';
import { ThreadColor } from './designs.js';
import { PublicFlow } from './flows.js';
import base62 from '@fry/base62';
import parseRGBA from 'color-rgba';
import { Maybe } from '../data.js';
import chroma from 'chroma-js';

type N = number;
export type ColorTriple = Triple<N>;
export type ColorTuple = [...ColorTriple, ...N[]];
export type CmykTuple = Four<N>;

export const colorEntrySize = {
	/** The usual. RGB, sRGB, etc. */
	rgb: 3,
	cmyk: 4,
	/** D50 (ICC, LCMS, Argyll) */
	lab: 3,
	/** D65 (Linshang, Oklab, sRGB?) */
	labd65: 3,
	/** HCL in Lab D50 */
	hcl: 3,
	/** The OK ones */
	oklab: 3,
	oklch: 3,
} as const;

export type ColorSpaceId = keyof typeof colorEntrySize;
export type ColorComponents<S extends ColorSpaceId> = (S extends 'cmyk' ? CmykTuple : ColorTriple);

export interface ColorComponentRanges {
	lightness: [number, number];
	chroma: [number, number];
	grayLightness: [number, number];
}

export type ColorRangesOverride = Maybe<Partial<ColorComponentRanges>>;

export const mainColorGroups = [

	'base',
	'primary',
	'secondary',
	'accent',
	'collar',
	
	'extra1',
	'extra2',
	'extra3',
	'extra4',
	'extra5',
	'extra6',
	'extra7',
	
] as const;
export type MainColorGroup = (typeof mainColorGroups)[number];

export const apparelSourceColors: ColorTriple[] = [

	[0xAA, 0xAA, 0xAA],
	[0xFF, 0x00, 0x00],
	[0x00, 0xFF, 0x00],
	[0x00, 0x00, 0xFF],
	[0xFF, 0x00, 0xFF],
	
	[0xFF, 0xFF, 0x00],
	[0x00, 0xFF, 0xFF],
	[0xFF, 0xAA, 0x00],
	[0xAA, 0x00, 0x00],
	[0x00, 0xAA, 0x00],
	[0x00, 0x00, 0xAA],
	[0xAA, 0x00, 0xAA],
	
];

export const popColorsMap: Record<string, ColorTriple> = {
	'#000000': [0, 0, 0],
	'#ffffff': [255, 255, 255],
	'#303437': [47, 51, 54],
	'#696a6d': [105, 106, 109],
	'#a3a094': [163, 160, 148],
	'#98a198': [152, 161, 152],
	'#3e2f2d': [62, 47, 45],
	'#8a4630': [138, 70, 47],
	'#c3a067': [195, 160, 103],
	'#fff4db': [252, 244, 220],
	'#bda882': [189, 168, 131],
	'#fed6a6': [253, 214, 166],
	'#37000e': [53, 0, 15],
	'#670f27': [104, 12, 38],
	'#9e182f': [158, 22, 46],
	'#ee3e80': [233, 66, 124],
	'#dd93b7': [221, 147, 183],
	'#ed1c24': [225, 40, 40],
	'#ef4123': [232, 70, 37],
	'#f47920': [244, 121, 30],
	'#fcb316': [251, 179, 21],
	'#79c153': [121, 193, 83],
	'#ede947': [236, 232, 70],
	'#ffd400': [255, 212, 0],
	'#2c553c': [42, 85, 59],
	'#425037': [66, 80, 54],
	'#009747': [19, 150, 71],
	'#00a695': [38, 163, 146],
	'#00002c': [15, 7, 40],
	'#000f29': [2, 14, 38],
	'#005083': [0, 80, 131],
	'#006db7': [0, 109, 178],
	'#63a8cc': [101, 168, 204],
	'#00a5d3': [58, 160, 201],
	'#413276': [62, 50, 115],
};

export const popColors = Object.values(popColorsMap);

export const initColorPreset: ColorTriple[] = [
	[47, 51, 54],
	[42, 85, 59],
	[105, 106, 109],
	[255, 255, 255],
	[255, 255, 255],
	[42, 85, 59],
	[105, 106, 109],
	[255, 255, 255],
	[42, 85, 59],
	[105, 106, 109],
	[255, 255, 255],
	[47, 51, 54],
];

export const findThreadColor = (within: ThreadColor[], id: string) => (
	within.find(tc => tc.id == id)
	?? within.find(tc => tc.reference == id)
);

export const screenPrintFallbacks: Record<string, string> = {
	// empty for now.
};

export const profileList: [string, number][] = [
	
	// basic
	['rgb', 1],
	['srgb', 1],
	
	// own
	['dtf', 51],
	
	// vendor
	['champro', 101],
	['oki', 102],
	['prosphere', 103],
	['ubix', 104],
	['frint', 105],

];
export const profileNameToId = Object.fromEntries(profileList);
export const profileIdToName = Object.fromEntries(profileList.map(([s, n]) => [n, s]));
export const colorIdVersion = 7;

export function isColorInProfile(color: Maybe<ColorTuple>, profile: string) {
	return color && color[10] && color[10] == profileNameToId[profile];
}

export const commandBasedGraphicsTypes = ['digitize', 'screen-print'];
export const isCommandBasedGraphics = (f: PublicFlow) => maybe(f.graphics, gt => commandBasedGraphicsTypes.includes(gt)) ?? false;

export function toRGB(hsl: ColorTuple, out?: ColorTriple): ColorTuple {
	const h = hsl[0] / 360;
	const s = hsl[1] / 100;
	const l = hsl[2] / 100;
	
	const rgb = out || [0, 0, 0];
	if (s === 0) {
		const val = Math.round(l * 255);
		rgb[0] = val;
		rgb[1] = val;
		rgb[2] = val;
		return rgb;
	}
	
	const t2 = l < 0.5 ? l * (1 + s) : l + s - l * s;
	const t1 = 2 * l - t2;
	
	for (let i = 0; i < 3; i++) {
		let t3 = h + 1 / 3 * -(i - 1);
		if (t3 < 0) {
			t3++;
		}
		else if (t3 > 1) {
			t3--;
		}
		
		let val: number;
		if (6 * t3 < 1) {
			val = t1 + (t2 - t1) * 6 * t3;
		}
		else if (2 * t3 < 1) {
			val = t2;
		}
		else if (3 * t3 < 2) {
			val = t1 + (t2 - t1) * (2 / 3 - t3) * 6;
		}
		else {
			val = t1;
		}
		
		rgb[i] = Math.round(val * 255);
	}
	
	return rgb;
}

export function getHCL(color: ColorTuple) {
	if (lengthEq(color, 12) && color[11] >= colorIdVersion) return color.slice(7, 10) as ColorTriple;
	const hcl = chroma.rgb(...color.slice(0, 3) as ColorTriple).hcl();
	return hcl.map(c => isFinite(c) ? c : 0) as ColorTriple;
}

export function toNaiveCMYK([r, g, b]: ColorTuple) {
	
	let c = 0;
	let m = 0;
	let y = 0;
	let k = 0;
	
	if (r == 0 && g == 0 && b == 0) {
		return [0, 0, 0, 1];
	}
	
	c = 1 - (r / 255);
	m = 1 - (g / 255);
	y = 1 - (b / 255);
	
	const minCMY = Math.min(c, Math.min(m, y));
	c = (c - minCMY) / (1 - minCMY);
	m = (m - minCMY) / (1 - minCMY);
	y = (y - minCMY) / (1 - minCMY);
	k = minCMY;
	
	return [c, m, y, k];
	
}

export function hclSimilarity(a: ColorTriple, b: ColorTriple) {
	const hd = a[1] && isFinite(a[0]) && b[1] && isFinite(b[0]) ? Math.abs(a[0] - b[0]) : 179;
	const cm = Math.max(a[1], b[1]) / 100;
	return (
		cm * Math.min(hd, 360 - hd) / 180
		+ (1 - cm) * Math.abs(a[2] - b[2]) / 100
		+ 0.5 * (1 - cm) * Math.abs(a[2] - b[2]) / 100
	);
};

export const parseRGB = (str: string) => parseRGBA(str).slice(0, 3) as Maybe<ColorTriple>;
export const rgbToHex = (rgb: number[]) => '#' + rgb.slice(0, 3).map(c => c.toString(16).padStart(2, '0')).join('');
export const isHexColor = (hex: string) => hex.length === 6 && !isNaN(Number(`0x${hex}`));

export function stringifyColorId(color: ColorTuple, profile?: string) {
	if (color.length == 3) return rgbToHex(color);
	if (color.length != 10 && color.length != 12) throw new Error('Invalid color tuple');
	const scaled = [
		...color.slice(0, 7), // RGB CMYK
		color[7]! % 360 / 2, // H
		...color.slice(8, 10), // SL
		color[10] || (profile ? profileNameToId[profile] : null) || 0,
		color[11] || 3, // v
	].map(n => Math.round(n));
	return '@' + base62.encode(scaled);
}

export function parseColorId(str: string): {
	color?: ColorTuple;
	profile?: string;
	version?: number;
	error?: any;
} {
	try {
		const scaled = Array.from(base62.decode(str[0] == '@' ? str.slice(1) : str));
		if ((scaled[11] || 0) >= 2) return {
			color: [...scaled.slice(0, 7), scaled[7]! * 2 % 360, ...scaled.slice(8)] as ColorTuple,
			profile: scaled[10] && profileIdToName[scaled[10]] || undefined,
			version: scaled[11],
		};
		if (lengthEq(scaled, 10)) return {
			color: [...scaled.slice(0, 7), scaled[7] * 4 % 360, ...scaled.slice(8)] as ColorTuple,
			version: 1,
		};
		console.error('Not a valid color ID', str, scaled);
		return {
			error: 'Not a valid color ID',
		};
	}
	catch (e) {
		console.error(e);
		return {
			error: e,
		};
	}
}
